"use client";
import { BalancesList } from "@/components/layout/balances";
import { GemsHistory } from "@/components/layout/gems-history";
import { InventorysList } from "@/components/layout/inventory";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePassportProvider } from "@/context";
import { useRightNav } from "@/context/RightNavContext";

import { PrizeWheelMenuItem } from "@/components/ui/custom/prize-wheel/PrizeWheelMenuItem";
import PrizeWheelV2Modal from "@/components/ui/custom/prize-wheel/PrizeWheelModal";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { useLoginModal } from "@/context/LoginModalContext";
import { useOnboardingModal } from "@/hooks/useOnboardingModal";
import { usePrizesWonFeed } from "@/hooks/usePrizesWonFeed";
import { Gem, Package, User, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { DailyQuests } from "../daily-quests";
import { RightNavHeader } from "./RightNavHeader";
import { WidgetTabContent } from "./balances-tab/WidgetTabContent";
import { WidgetsMenu } from "./balances-tab/WidgetsMenu";

interface RightNavProps {
  className?: string;
  signInCallback: () => void;
}

export function RightNav({ className, signInCallback }: RightNavProps) {
  const {
    isCollapsed,
    isInitialLoad,
    toggleCollapse,
    widgetConfig,
    closeWidgetTab,
    isWidgetOpen,
  } = useRightNav();
  const { isLoggedIn, userInfo } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const { isOnboardingModalEnabled } = useOnboardingModal();
  const {
    currentPrize,
    isTransitioning,
    isLoading: isPrizesLoading,
    spinsAvailable,
  } = usePrizesWonFeed();
  const { trackV3 } = useAnalytics();

  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const savedTab = sessionStorage.getItem("rightNavActiveTab");
      return savedTab || "balances";
    }
    return "balances";
  });

  const [isPrizeWheelModalOpen, setIsPrizeWheelModalOpen] = useState(false);

  const handleTabChange = (value: string) => {
    trackV3({
      object: "Navigation",
      action: Action.PRESSED,
      extras: {
        section: "RightNav",
        tab: value,
      },
    });
    if (!isLoggedIn) {
      isOnboardingModalEnabled ? signInCallback() : openLoginModal();
    } else {
      setActiveTab(value);
      sessionStorage.setItem("rightNavActiveTab", value);
    }
  };

  useEffect(() => {
    const savedTab = sessionStorage.getItem("rightNavActiveTab");
    if (!savedTab && isLoggedIn) {
      setActiveTab("quests");
      sessionStorage.setItem("rightNavActiveTab", "quests");
    }
  }, [isLoggedIn]);

  return (
    <TooltipProvider>
      <aside
        className={`
          h-full border-l border-white/20 shadow-xl backdrop-blur-xl
          fixed right-0 top-0 z-50 md:static md:z-auto
          bg-black/70 md:bg-transparent
          ${!isInitialLoad ? "transition-all duration-300 ease-out md:ease-in-out" : ""}
          ${
            isCollapsed
              ? "w-full md:w-12 translate-x-full md:translate-x-0"
              : "w-full md:w-[360px] translate-x-0"
          }
          ${className || ""}
        `}
      >
        <div className="bg-transparent overflow-y-auto overflow-x-hidden h-full">
          {isCollapsed && (
            <div className="flex flex-col items-center">
              <div className="hidden md:flex justify-center pt-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white/80 hover:bg-white/20 hover:text-white h-8 w-8 rounded-full"
                  onClick={toggleCollapse}
                >
                  {isLoggedIn ? (
                    <div className="relative">
                      <Avatar className="w-6 h-6 border border-white/30 shadow-lg">
                        <AvatarImage
                          src="/images/profile/spaceman.png"
                          alt={userInfo?.nickname || "User"}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-white/20 backdrop-blur-md text-white font-semibold border border-white/30 text-xs">
                          {(userInfo?.nickname || userInfo?.email || "U")
                            .slice(0, 2)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                </Button>
              </div>

              {isLoggedIn && (
                <div className="space-y-3 mt-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg mx-auto
                                   text-white/90 hover:text-white hover:bg-white/10
                                   transition-all duration-300"
                        onClick={() => {
                          setActiveTab("quests");
                          toggleCollapse();
                        }}
                        type="button"
                        aria-label="Open quests tab"
                      >
                        <Gem className="w-5 h-5 text-purple-300" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Rewards</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg mx-auto
                                   text-white/90 hover:text-white hover:bg-white/10
                                   transition-all duration-300"
                        onClick={() => {
                          setActiveTab("balances");
                          toggleCollapse();
                        }}
                        type="button"
                        aria-label="Open balances tab"
                      >
                        <Wallet className="w-5 h-5 text-green-300" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Balances</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg mx-auto
                                   text-white/90 hover:text-white hover:bg-white/10
                                   transition-all duration-300"
                        onClick={() => {
                          setActiveTab("inventory");
                          toggleCollapse();
                        }}
                        type="button"
                        aria-label="Open collections tab"
                      >
                        <Package className="w-5 h-5 text-yellow-300" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>Inventory</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          )}

          {!isCollapsed && (
            <div className="bg-transparent h-full">
              {/* Content container with smooth transitions */}
              <div className="relative h-full flex flex-col">
                {/* Normal content */}
                <div
                  className={`
                    flex flex-col h-full transition-all duration-300 ease-in-out
                    ${
                      isWidgetOpen
                        ? "-translate-x-full opacity-0"
                        : "translate-x-0 opacity-100"
                    }
                  `}
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  <RightNavHeader
                    onToggleCollapse={toggleCollapse}
                    signInCallback={
                      isOnboardingModalEnabled ? signInCallback : openLoginModal
                    }
                  />

                  <div className="pt-6 flex-1 flex flex-col min-h-0">
                    <Tabs
                      value={activeTab}
                      onValueChange={handleTabChange}
                      className="flex flex-col h-full"
                    >
                      <TabsList
                        variant="glass"
                        className="grid grid-cols-3 mx-4"
                      >
                        <TabsTrigger value="quests">Rewards</TabsTrigger>
                        <TabsTrigger value="balances">Balances</TabsTrigger>
                        <TabsTrigger value="inventory">Inventory</TabsTrigger>
                      </TabsList>

                      <TabsContent
                        value="quests"
                        className="px-4 mt-4 flex-1 overflow-y-auto overflow-x-hidden min-h-0"
                      >
                        {isLoggedIn && (
                          <>
                            <div className="my-4">
                              <PrizeWheelMenuItem
                                walletAddress={currentPrize?.wallet_address}
                                spinsRemaining={spinsAvailable}
                                prizeName={currentPrize?.name}
                                tier={currentPrize?.tier}
                                isTransitioning={isTransitioning}
                                onClick={() => setIsPrizeWheelModalOpen(true)}
                              />
                            </div>
                            <DailyQuests />
                          </>
                        )}
                        <div className="mt-2">
                          <GemsHistory />
                        </div>
                      </TabsContent>

                      <TabsContent
                        value="balances"
                        className="px-4 flex-1 overflow-y-auto overflow-x-hidden min-h-0"
                      >
                        <div
                          className={
                            !isLoggedIn
                              ? "blur-[2px] opacity-70 pointer-events-none"
                              : ""
                          }
                        >
                          <WidgetsMenu isDrawerMenu={true} section="RightNav" />
                          <BalancesList />
                        </div>
                      </TabsContent>

                      <TabsContent
                        value="inventory"
                        className="px-4 mt-4 flex-1 overflow-y-auto overflow-x-hidden min-h-0"
                      >
                        <InventorysList />
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>

                {/* Tab content */}
                <div
                  className={`
                      absolute inset-0 transition-all duration-300 ease-in-out
                      ${
                        isWidgetOpen
                          ? "translate-x-0 opacity-100"
                          : "translate-x-full opacity-0"
                      }
                    `}
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  <WidgetTabContent
                    widgetConfig={widgetConfig}
                    closeWidgetTab={closeWidgetTab}
                    toggleCollapse={toggleCollapse}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Prize Wheel Modal */}
      <PrizeWheelV2Modal
        open={isPrizeWheelModalOpen}
        onOpenChange={setIsPrizeWheelModalOpen}
        spinsAvailable={spinsAvailable}
      />
    </TooltipProvider>
  );
}
